import React from 'react';
import { Loader2, Bot, User, Sparkles, Wrench, CheckCircle, XCircle } from 'lucide-react';
import { ToolCallsGroup } from './ToolCallDisplay';
import { StatusChangeApproval } from './StatusChangeApproval';
import { RateLimitApproval } from './RateLimitApproval';
import { AgentLoopError } from './AgentLoopError';
import { StreamingMessage } from './StreamingMessage';
import { ThinkingBlock } from './ThinkingBlock';
import { MarkdownContent } from '../shared/MarkdownContent';
import { ToolCall, ToolResult } from '../../hooks/useWorkflowChat';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool_use' | 'tool_result';
  content: string;
  timestamp: Date;
  thinking?: string | null;
  toolCalls?: ToolCall[];
  toolResults?: ToolResult[];
}

interface BotChatMessagesProps {
  /** List of messages to display */
  messages: Message[];
  /** Whether chat history is loading */
  isLoadingHistory: boolean;
  /** Whether a message is currently being sent */
  isLoading: boolean;
  /** Status text to display while processing */
  loopingStatus?: string;
  /** Pending approval for status change */
  pendingApproval?: ToolCall | null;
  /** Name of the node for pending approval */
  pendingNodeName?: string;
  /** Rate limit approval state */
  rateLimitApproval?: {
    allowedCount: number;
    blockedToolCalls: ToolCall[];
    resourceType: string;
  } | null;
  /** Error state for agent loop failures */
  agentError?: string | null | undefined;
  /** Context type (stream or global) */
  context: 'stream' | 'global';
  /** Stream name for stream context */
  streamName?: string;
  /** Suggested questions to display when empty */
  suggestedQuestions: string[];
  /** Callback when approval is granted */
  onApprove: () => void;
  /** Callback when approval is rejected */
  onReject: () => void;
  /** Callback when rate limit is approved */
  onRateLimitApproval: (allow: boolean) => void;
  /** Callback when suggested question is clicked */
  onSuggestedQuestionClick: (question: string) => void;
  /** Callback when error retry is clicked */
  onRetryError?: () => void;
  /** Callback when error dismiss is clicked */
  onDismissError?: () => void;
  /** Streaming content for in-progress message */
  streamingContent?: string;
  /** Streaming thinking for in-progress message */
  streamingThinking?: string;
  /** Ref to scroll to end */
  messagesEndRef: React.RefObject<HTMLDivElement>;
}

/**
 * Message list component for BotChat.
 * Displays messages, approvals, and loading states.
 */
export function BotChatMessages({
  messages,
  isLoadingHistory,
  isLoading,
  loopingStatus,
  pendingApproval,
  pendingNodeName,
  rateLimitApproval,
  agentError,
  context,
  streamName,
  suggestedQuestions,
  onApprove,
  onReject,
  onRateLimitApproval,
  onSuggestedQuestionClick,
  onRetryError,
  onDismissError,
  streamingContent = '',
  streamingThinking = '',
  messagesEndRef,
}: BotChatMessagesProps) {
  if (isLoadingHistory) {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-6 h-6 animate-spin text-text-muted" />
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        <div className="h-full flex flex-col items-center justify-center text-center">
          <div className="w-12 h-12 rounded-full bg-surface-800 flex items-center justify-center mb-4">
            <Sparkles className="w-6 h-6 text-primary-400" />
          </div>
          <p className="text-text-secondary text-sm mb-4">
            {context === 'stream'
              ? `Ask me anything about "${streamName}"`
              : 'Ask me anything about your streams'}
          </p>
          <div className="space-y-2 w-full">
            {suggestedQuestions.map((question, i) => (
              <button
                key={i}
                onClick={() => onSuggestedQuestionClick(question)}
                className="w-full text-left px-3 py-2 bg-surface-800 hover:bg-surface-700 rounded-lg text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
      {messages.map((message) => {
        const isToolMessage = message.role === 'tool_use' || message.role === 'tool_result';
        return (
        <div
          key={message.id}
          className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
        >
          <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
            message.role === 'user' ? 'bg-primary-500/20'
            : isToolMessage ? 'bg-surface-600'
            : 'bg-surface-700'
          }`}>
            {message.role === 'user' ? (
              <User className="w-3.5 h-3.5 text-primary-400" />
            ) : message.role === 'tool_use' ? (
              <Wrench className="w-3.5 h-3.5 text-amber-400" />
            ) : message.role === 'tool_result' ? (
              message.content.startsWith('Error') || message.content.startsWith('error')
                ? <XCircle className="w-3.5 h-3.5 text-red-400" />
                : <CheckCircle className="w-3.5 h-3.5 text-success-400" />
            ) : (
              <Bot className="w-3.5 h-3.5 text-text-muted" />
            )}
          </div>
          <div className={`flex-1 max-w-[85%] ${message.role === 'user' ? 'text-right' : ''}`}>
            <div className={`inline-block rounded-lg px-3 py-2 text-sm ${
              message.role === 'user'
                ? 'bg-primary-500/20 text-text-primary'
                : isToolMessage
                ? 'bg-surface-700/50 text-text-secondary font-mono text-xs'
                : 'bg-surface-800 text-text-primary'
            }`}>
              {message.role === 'assistant' ? (
                <>
                  {message.thinking && <ThinkingBlock content={message.thinking} />}
                  <MarkdownContent content={message.content} />
                </>
              ) : isToolMessage ? (
                <details className="cursor-pointer">
                  <summary className="text-text-muted select-none">
                    {message.role === 'tool_use' ? 'Tool Call' : 'Tool Result'}
                    {message.content.length > 100 ? ` (${message.content.length} chars)` : ''}
                  </summary>
                  <pre className="mt-1 whitespace-pre-wrap break-all max-h-40 overflow-y-auto">
                    {message.content}
                  </pre>
                </details>
              ) : (
                <p className="whitespace-pre-wrap">{message.content}</p>
              )}
              {/* Show tool calls */}
              {message.toolCalls && message.toolCalls.length > 0 && (
                <ToolCallsGroup
                  toolCalls={message.toolCalls}
                  results={message.toolResults || []}
                />
              )}
            </div>
          </div>
        </div>
        );
      })}
      {/* Pending approval UI */}
      {pendingApproval && (
        <StatusChangeApproval
          toolCall={pendingApproval}
          nodeName={pendingNodeName}
          onApprove={onApprove}
          onReject={onReject}
        />
      )}
      {/* Rate limit approval UI */}
      {rateLimitApproval && (
        <RateLimitApproval
          allowedCount={rateLimitApproval.allowedCount}
          blockedToolCalls={rateLimitApproval.blockedToolCalls}
          resourceType={rateLimitApproval.resourceType as "node" | "edge"}
          onAllow={() => onRateLimitApproval(true)}
          onSkip={() => onRateLimitApproval(false)}
        />
      )}
      {/* Agent error UI */}
      {agentError && onDismissError && (
        <AgentLoopError
          error={agentError}
          onRetry={onRetryError}
          onDismiss={onDismissError}
        />
      )}
      {isLoading && (streamingContent || streamingThinking) ? (
        <StreamingMessage content={streamingContent} thinking={streamingThinking} />
      ) : isLoading ? (
        <div className="flex gap-3">
          <div className="w-8 h-8 rounded-full bg-surface-700 flex items-center justify-center">
            <Bot className="w-4 h-4 text-text-muted" />
          </div>
          <div className="bg-surface-800 rounded-lg px-3 py-2 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-text-muted" />
            {loopingStatus && (
              <span className="text-sm text-text-muted">{loopingStatus}</span>
            )}
          </div>
        </div>
      ) : null}
      <div ref={messagesEndRef} />
    </div>
  );
}
